import { Box, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { CategoryPageContext } from './CategoryPageContext';
import { ProductMediaContext, ProductPageContext } from './ProductPageContext';
import { RenderBlockElement } from './PageComponentElementBlocks';
import ProductPageBody from './ProductPageBody';
import { normalizeProductLayout } from './productPageLayout';
import { useAppDispatch, useAppSelector } from '../reduxhooks';
import { setPreviewCollection, setPreviewProduct } from '../redux/ProductsListRedux';
import {
  buildPreviewCollection, mapApiProductToProductOnUser, PREVIEW_COLLECTION_HANDLE,
} from '../terbebasApi';

// Dashboard live-preview surface. The prodpanel LayoutEditor embeds this
// route in an iframe and posts draft UIBlockElement[] via postMessage;
// they render through the SAME RenderBlockElement pipeline (and catalog
// redux state) the storefront itself uses — so the preview cannot drift
// from what shoppers see. Device switching needs no code here: the editor
// resizes the iframe and the storefront's own breakpoint (sm) decides
// desktop vs mobile content, exactly like a real viewport.
export default function PagePreviewBlocks() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const [elements, setElements] = useState<any[]>([]);
  // Kategori drafts post their Koleksi handles so BlockCollectionProducts
  // has a category context to resolve (empty for Koleksi/Halaman drafts).
  const [collectionHandles, setCollectionHandles] = useState<string[]>([]);
  // The draft's own product selection, registered as a synthetic collection so
  // BlockCollectionProducts renders it even when the draft is unsaved (or new,
  // and therefore has no server-side handle yet).
  const [hasPreviewProducts, setHasPreviewProducts] = useState(false);
  // Product-template drafts render as a product detail page instead.
  const [previewKind, setPreviewKind] = useState<'category' | 'product'>('category');
  const [previewProductId, setPreviewProductId] = useState('');
  // Raw admin-shaped product from the dashboard, kept in state rather than
  // dispatched inline: the catalog's fulfilled reducer REPLACES
  // productsDictionary wholesale, so a product injected before the catalog
  // resolves would be silently dropped. Re-injecting whenever either changes
  // makes the order irrelevant.
  const [previewProductRaw, setPreviewProductRaw] = useState<any>(null);
  // Gallery state, mirroring what the real product page owns.
  const [mediaIndex, setMediaIndex] = useState(0);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const productsDictionary = useAppSelector((state) => state.productsList.productsDictionary);
  const catalogStatus = useAppSelector((state) => state.productsList.status);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Draft layouts are public catalog data; no origin restriction needed.
      if (event.data?.type === 'terbebas-preview-blocks' && Array.isArray(event.data.elements)) {
        setElements(event.data.elements);
        setCollectionHandles(Array.isArray(event.data.categoryCollectionHandles)
          ? event.data.categoryCollectionHandles : []);
        const previewProductIds = Array.isArray(event.data.previewProductIds)
          ? event.data.previewProductIds : [];
        dispatch(setPreviewCollection(buildPreviewCollection(previewProductIds)));
        setHasPreviewProducts(previewProductIds.length > 0);
        setPreviewKind(event.data.previewKind === 'product' ? 'product' : 'category');
        setPreviewProductId(event.data.previewProductId ?? '');
        setPreviewProductRaw(event.data.previewProduct ?? null);
      }
    };
    window.addEventListener('message', onMessage);
    // Ask the parent editor for the current draft (covers both load orders).
    window.parent?.postMessage({ type: 'terbebas-preview-ready' }, '*');
    return () => window.removeEventListener('message', onMessage);
  }, [dispatch]);

  // The dashboard sends the product it is editing in the admin API's shape.
  // Mapping it through the same function the real catalog uses lets the
  // preview render DRAFT products (absent from the public, ACTIVE-only
  // catalog) and unsaved edits — without the storefront fetching anything a
  // shopper couldn't already see.
  useEffect(() => {
    if (!previewProductRaw) return;
    try {
      dispatch(setPreviewProduct(mapApiProductToProductOnUser(previewProductRaw)));
    } catch {
      // A malformed draft must not take the whole preview down.
    }
  }, [dispatch, previewProductRaw, catalogStatus]);

  // Assigned Koleksi first, then the draft's own products — mirroring how a
  // saved Kategori renders its Koleksi plus its direct-products Koleksi.
  const contextHandles = hasPreviewProducts
    ? [...collectionHandles, PREVIEW_COLLECTION_HANDLE]
    : collectionHandles;

  // A product template belongs to no single product, so the editor names one
  // to preview against; fall back to any loaded product so a fresh template
  // still renders something. Only ACTIVE products are in this catalog — the
  // preview is a real storefront instance.
  const product = previewProductId
    ? productsDictionary[previewProductId]
    : productsDictionary[Object.keys(productsDictionary)[0]];

  const productLayout = useMemo(
    () => normalizeProductLayout(elements),
    [elements]);

  // Memoised: `?? []` would mint a new array every render, and this feeds
  // the media context's dependency list.
  const mediaUrls = useMemo(() => product?.productImages ?? [], [product]);
  const mediaContext = useMemo(() => ({
    mediaUrls,
    index: mediaIndex,
    setIndex: setMediaIndex,
    overlayOpen,
    setOverlayOpen,
    // No autoslide in the preview — a moving carousel while the merchant is
    // editing block settings is a distraction, not a fidelity win.
    freezeAutoslide: () => {},
  }), [mediaUrls, mediaIndex, overlayOpen]);

  const clickShield = { onClickCapture: (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); } };

  if (previewKind === 'product') {
    if (!product) {
      return (
        <Box sx={{ p: 4 }}>
          <Typography variant="caption" color="textSecondary">
            Belum ada produk untuk pratinjau.
          </Typography>
        </Box>
      );
    }
    return (
      <ProductPageContext.Provider
        value={{ productId: product.productId, product, isPreview: true }}>
        <ProductMediaContext.Provider value={mediaContext}>
          {/* Clicks are swallowed so links and add-to-cart can't act from
              inside the editor. */}
          <Box {...clickShield}>
            <ProductPageBody layout={productLayout} />
          </Box>
        </ProductMediaContext.Provider>
      </ProductPageContext.Provider>
    );
  }

  return (
    // Clicks are swallowed so product links can't navigate the preview
    // away from this route; hover states still work.
    <CategoryPageContext.Provider value={{ categoryId: '__preview__', collectionHandles: contextHandles }}>
      <Box {...clickShield}>
        {elements.map((block: any, i: number) => (
          <RenderBlockElement
            key={i}
            code={isDesktop ? block.elementDesktopCode : block.elementMobileCode}
            contentJson={isDesktop ? block.elementDesktopContentJson : block.elementMobileContentJson}
          />
        ))}
      </Box>
    </CategoryPageContext.Provider>
  );
}
